// AC GUIDE 10: VRV SYSTEM FOR HOME, WHICH ONE AND HOW MUCH? (5 Nov 2026)
// /blog/vrv-system-for-home
//
// Written to GUIDES_PLAYBOOK.md section 3, in the new layout (guide 7
// onwards). Guide 7 (/blog/vrv-vs-split-ac) answers "VRV or split?"; this one
// answers the next question, "which VRV, and what does it cost?", and links
// back to it. One topic, one page.
//
// THE ANSWER IS SAHEB'S (5 Nov 2026): for flats, where there is no roof and
// space is tight, VRV Home and VRV S are the best options. Where the capacity
// and use are not high, VRV Home; it stops at 6 HP and has limited indoor
// options. VRV S goes up to 12 HP with every indoor option. VRV Alpha for a
// high-capacity house with space for outdoor units, the latest technology and
// very high use (running fully, all day). Size room by room with the AC
// calculator, but plan the whole VRV system in a consultation. Cost: on
// average about ₹65,000 to ₹70,000 per HP with execution (25 Oct 2026).
//
// Product facts: Daikin India's VRV Home, VRV S and VRV Alpha pages and the
// VRV Alpha catalogue 2026. Consultation prices come from data/consultation.js
// through the caller. No imports. **double stars** mean bold.

export const VRVH_SLUG = 'blog/vrv-system-for-home';
export const VRVH_PUBLISHED = '2026-11-05';
export const VRVH_UPDATED = '2026-11-05';

export const VRVH_SEO = {
  title: 'VRV System for Home: VRV Home, VRV S or VRV Alpha? Cost | Citi Cool',
  meta: 'Which VRV system for your home? VRV Home for a small flat, VRV S for a bigger one, VRV Alpha for a large house used all day. Sizes, indoor units and cost per HP.',
  eyebrow: 'AC Guide · Buying',
  h1: 'VRV system for home: which one, and how much?',
  crumb: 'VRV system for home',
  readMins: 4,
};

// Daikin India product pages (Nov 2026). kW is Daikin's cooling capacity.
export const VRV_HOME_SIZES = [{ hp: 4, kw: 11.2 }, { hp: 5, kw: 14.0 }, { hp: 6, kw: 16 }];
export const VRV_S_SIZES = [{ hp: 4, kw: 11.2 }, { hp: 5, kw: 14 }, { hp: 6, kw: 16 }, { hp: 8, kw: 22.4 }, { hp: 10, kw: 28 }, { hp: 12, kw: 33.5 }];
export const VRV_ALPHA_RANGE = { minHp: 6, maxHp: 78, minKw: 16, maxKw: 219 };
export const VRV_HOME_INDOORS = 8;     // indoor units on one VRV Home outdoor unit
export const VRV_HOME_PIPE_M = 40;     // VRV Home: actual pipe length up to 40 m
export const ALPHA_INDOOR_TYPES = 16;  // VRV Alpha catalogue: 16 types, 77 models
export const PER_HP = [65000, 70000];  // Saheb, 25 Oct 2026: average, with execution

// ─── sources ──────────────────────────────────────────────────────────────

export const VRVH_SOURCES = [
  { key: 'saheb', label: 'Our showroom advice on VRV for homes: VRV Home or VRV S for flats, VRV Alpha for large houses used all day; size room by room, plan in a consultation (November 2026)', by: 'Citi Cool', href: null },
  { key: 'saheb-cost', label: 'Our average VRV cost: about ₹65,000 to ₹70,000 per HP, with execution (October 2026)', by: 'Citi Cool', href: null },
  { key: 'daikin-home', label: 'VRV Home: 4, 5 and 6 HP on single-phase power, up to 8 indoor units, wall and duct indoor units, 40 m pipe, outdoor unit on a balcony or wall', by: 'Daikin India', href: 'https://daikinindia.com/products-services/vrv-air-conditioner/vrv-home/vrv-home' },
  { key: 'daikin-s', label: 'VRV S: 4 to 12 HP models (11.2 to 33.5 kW), single-phase and three-phase', by: 'Daikin India', href: 'https://daikinindia.com/products-services/vrv-air-conditioner/vrv-s/vrv-s' },
  { key: 'daikin-alpha', label: 'Daikin VRV Alpha: "for commercial and large residential applications"', by: 'Daikin India', href: 'https://daikinindia.com/daikin-vrv-alpha/' },
  { key: 'alpha-specs', label: 'VRV Alpha: 6 to 78 HP (16 to 219 kW), three-phase, ISEER up to 11.44', by: 'Daikin India', href: 'https://daikinindia.com/products-services/daikin-vrv-alpha' },
  { key: 'alpha-cat', label: 'VRV Alpha catalogue 2026: 16 indoor unit types, Reiri for Home app, DAi-edge monitoring, night quiet mode, backup operation', by: 'Daikin India', href: 'https://daikinindia.com/pdf/VRV-Alpha-catalogue-2026.pdf' },
  { key: 'bee-iseer', label: 'Indian Seasonal Energy Efficiency Ratio', by: 'Vikaspedia, Government of India', href: 'https://en.vikaspedia.in/viewcontent/energy/policy-support/energy-efficiency/indian-seasonal-energy-efficiency-ratio' },
  { key: 'calc', label: 'Our AC calculator: the size each room needs, on Daikin\'s heat-load method', by: 'Citi Cool engineering', href: '/ac-calculator' },
  { key: 'consult', label: 'HVAC design and consultation: video call or site visit with our engineer', by: 'Citi Cool', href: '/hvac-consultant' },
  { key: 'units', label: 'Our indoor unit corner: every Daikin VRV indoor unit, with its look and finish', by: 'Citi Cool', href: '/architects-interior-designers#units' },
];

// ─── arithmetic ───────────────────────────────────────────────────────────

const KW_PER_TR = 3.517;
export const tonOf = (kw) => Math.round((kw / KW_PER_TR) * 2) / 2;   // nearest half ton
export const lakh = (n) => `₹${(Math.round(n / 10000) / 10).toLocaleString('en-IN')} lakh`;
const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const costRange = (hp) => `${lakh(hp * PER_HP[0])} to ${lakh(hp * PER_HP[1])}`;
const listText = (xs) => xs.join(', ').replace(/, ([^,]*)$/, ' or $1');

// ─── the page ─────────────────────────────────────────────────────────────

// consult: { call, visit } prices from CONSULT_SERVICES in data/consultation.js.
export function vrvHomeContent(consult = {}) {
  const homeMax = VRV_HOME_SIZES[VRV_HOME_SIZES.length - 1];
  const sMax = VRV_S_SIZES[VRV_S_SIZES.length - 1];
  const sMin = VRV_S_SIZES[0];
  const homeTons = listText(VRV_HOME_SIZES.map((u) => `${tonOf(u.kw)}`));
  const call = consult.call ? inr(consult.call) : null;
  const visit = consult.visit ? inr(consult.visit) : null;

  return {
    answer: {
      text: '**For a flat with light use, choose VRV Home. For more rooms or more use, choose VRV S. For a large house used all day, choose VRV Alpha.**',
      src: ['saheb'],
    },
    because: [
      { text: `VRV Home goes up to ${homeMax.hp} HP. It has fewer indoor unit styles.`, src: ['daikin-home', 'saheb'] },
      { text: `VRV S goes up to ${sMax.hp} HP, with every indoor unit style.`, src: ['daikin-s', 'saheb'] },
      { text: 'VRV Alpha is Daikin\'s biggest range, built for large homes and heavy use.', src: ['daikin-alpha', 'saheb'] },
    ],
    watch: {
      title: 'Watch: which VRV fits which home',
      captions: [
        { at: 0, text: 'A flat with no roof of its own. The outdoor unit must fit on a balcony.' },
        { at: 0.25, text: `A small flat, AC on a few hours: VRV Home, up to ${homeMax.hp} HP.` },
        { at: 0.5, text: `More rooms, or a certain look in each room: VRV S, up to ${sMax.hp} HP.` },
        { at: 0.75, text: 'A large house with space outside, AC on all day: VRV Alpha.' },
      ],
      note: `A simple drawing. HP is how VRV is sized: ${homeMax.hp} HP is about ${tonOf(homeMax.kw)} ton.`,
      noteSrc: ['daikin-home', 'saheb'],
    },
    table: {
      title: 'Which VRV system is best for your home?',
      head: ['Your home', 'Choose'],
      rows: [
        ['Flat, AC on a few hours', 'VRV Home'],
        ['Flat or floor with more rooms or more use', 'VRV S'],
        ['A certain indoor unit look in every room', 'VRV S'],
        ['Large house or villa with space outside', 'VRV Alpha'],
        ['AC running fully, most of the day', 'VRV Alpha'],
      ],
      note: 'The rule: **small flat, VRV Home. Bigger flat, VRV S. Large house used all day, VRV Alpha.** Still deciding between VRV and split ACs? Read [VRV or split AC](/blog/vrv-vs-split-ac) first.',
      src: ['saheb'],
    },
    example: {
      title: 'In rupees: what a VRV system costs',
      lines: [
        { label: `VRV Home, ${homeMax.hp} HP (about ${tonOf(homeMax.kw)} ton), with execution`, value: costRange(homeMax.hp) },
        { label: `VRV S, ${sMax.hp} HP (about ${tonOf(sMax.kw)} ton), with execution`, value: costRange(sMax.hp) },
      ],
      text: `This is our average: about ${inr(PER_HP[0])} to ${inr(PER_HP[1])} per HP, with execution. Your home decides the final price. Our engineer quotes it after seeing your plan.`,
      src: ['saheb-cost', 'daikin-home', 'daikin-s'],
    },
    sides: [
      {
        key: 'do',
        title: 'Planning VRV for your home: do\'s and don\'ts',
        goodHead: 'Do',
        badHead: 'Don\'t',
        good: [
          { text: 'Size each room first with our [AC calculator](/ac-calculator).', src: ['calc', 'saheb'] },
          { text: 'Plan the whole system with our engineer before you buy.', src: ['saheb', 'consult'] },
          { text: 'Plan it before your home is built or redone.', src: ['saheb'] },
          { text: 'Check where the outdoor unit goes: a balcony, a wall or the roof.', src: ['daikin-home', 'saheb'] },
        ],
        bad: [
          { text: `Don't pick VRV Home for more than ${homeMax.hp} HP. That is its limit.`, src: ['daikin-home'] },
          { text: 'Don\'t pick VRV Home if you want ceiling cassettes. It has wall and duct units only.', src: ['daikin-home', 'saheb'] },
          { text: 'Don\'t pick VRV Alpha for a small flat with no outdoor space.', src: ['saheb'] },
        ],
      },
    ],
    parts: [
      {
        id: 'vrv-home',
        q: 'What is Daikin VRV Home?',
        bullets: [
          { text: `It comes in ${listText(VRV_HOME_SIZES.map((u) => u.hp))} HP. That is about ${homeTons} ton.`, src: ['daikin-home'] },
          { text: `One outdoor unit runs up to ${VRV_HOME_INDOORS} indoor units, on normal home power.`, src: ['daikin-home'] },
          { text: 'The outdoor unit fits on a balcony, or hangs on a wall.', src: ['daikin-home'] },
          { text: 'Indoors you get wall units or hidden ceiling ducts only.', src: ['daikin-home', 'saheb'] },
        ],
      },
      {
        id: 'vrv-s',
        q: 'What is Daikin VRV S?',
        bullets: [
          { text: `It comes in ${sMin.hp} to ${sMax.hp} HP. That is about ${tonOf(sMin.kw)} to ${tonOf(sMax.kw)} ton.`, src: ['daikin-s'] },
          { text: 'It takes every VRV indoor unit style, ceiling cassettes too.', src: ['saheb'] },
          { text: 'See them all in our [indoor unit corner](/architects-interior-designers#units).', src: ['units'] },
        ],
      },
      {
        id: 'vrv-alpha',
        q: 'What is Daikin VRV Alpha?',
        bullets: [
          { text: `It runs from ${VRV_ALPHA_RANGE.minHp} to ${VRV_ALPHA_RANGE.maxHp} HP, on three-phase power.`, src: ['alpha-specs'] },
          { text: 'Daikin builds it for offices and large homes.', src: ['daikin-alpha'] },
          { text: 'Its ISEER goes up to about 11. ISEER is like a car\'s mileage.', src: ['alpha-specs', 'bee-iseer'] },
          { text: 'You can run it from Daikin\'s Reiri for Home app on your phone.', src: ['alpha-cat'] },
        ],
      },
      {
        id: 'cost',
        q: 'How much does a VRV system cost for a home?',
        bullets: [
          { text: `On average, about ${inr(PER_HP[0])} to ${inr(PER_HP[1])} per HP, with execution.`, src: ['saheb-cost'] },
          { text: `A ${homeMax.hp} HP VRV Home costs about ${costRange(homeMax.hp)}.`, src: ['saheb-cost', 'daikin-home'] },
          { text: 'Compare it with split ACs in our [VRV or split guide](/blog/vrv-vs-split-ac).', src: [] },
        ],
      },
      {
        id: 'size',
        q: 'What size VRV does my home need?',
        bullets: [
          { text: 'Start room by room. Our [AC calculator](/ac-calculator) gives each room\'s size.', src: ['calc', 'saheb'] },
          { text: 'Floor and sun change the size. Our [size guide](/blog/what-size-ac-do-i-need) shows how.', src: ['calc'] },
          { text: 'Then plan the whole system with our engineer.', src: ['saheb'] },
          { text: `${call ? `A video call costs ${call}.` : 'Book a video call.'}${visit ? ` A site visit costs ${visit}.` : ''} The fee comes off your order.`, src: ['consult'] },
        ],
      },
    ],
    fits: [
      { label: 'Plan it', title: 'Talk to our HVAC engineer', text: call ? `Video call ${call}, fee adjusted against your order.` : 'Video call or site visit.', href: '/hvac-consultant' },
      { label: 'See it running', title: 'Daikin VRV at our experience centre', text: 'South Extension Part-2, by appointment.', href: '/daikin-vrv-systems' },
    ],
    faq: [
      {
        q: 'Which VRV system is best for a home?',
        a: 'VRV Home for a small flat. VRV S for a bigger flat or more use. VRV Alpha for a large house used all day.',
        src: ['saheb'],
      },
      {
        q: 'What is the biggest VRV Home size?',
        a: `${homeMax.hp} HP, about ${tonOf(homeMax.kw)} ton. For more, choose VRV S, up to ${sMax.hp} HP.`,
        src: ['daikin-home', 'daikin-s'],
      },
      {
        q: 'How many indoor units can VRV Home run?',
        a: `Up to ${VRV_HOME_INDOORS} indoor units on one outdoor unit.`,
        src: ['daikin-home'],
      },
      {
        q: 'Does a home VRV need three-phase power?',
        a: 'Not VRV Home. It runs on normal single-phase power. VRV Alpha needs three-phase power.',
        src: ['daikin-home', 'alpha-specs'],
      },
      {
        q: 'Is VRV Alpha used in homes?',
        a: 'Yes, in large homes. Daikin builds it for commercial and large residential use.',
        src: ['daikin-alpha'],
      },
      {
        q: 'How much does VRV cost per HP?',
        a: `About ${inr(PER_HP[0])} to ${inr(PER_HP[1])} per HP on average, with execution. Your home decides the final price.`,
        src: ['saheb-cost'],
      },
    ],
  };
}
